// CharacterEditModal — edit a character sheet's core fields after creation:
// name, alignment, base ability scores, skill proficiencies & expertise, feats,
// max-HP override, current HP, and notes. Posts to /character/{id}/edit; the
// returned sheet (with recomputed derived stats) flows back via onSaved.
import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { EditCharacterPatch, FullCharacter, AbilityKey } from "../lib/types";
import { ABILITY_KEYS } from "../lib/types";
import { ABILITY_ABBR, abilityMod, signed, titleizeSlug } from "../lib/format";
import { SKILL_LABELS } from "../lib/srd";
import { Modal } from "./ui";
import { useToast } from "../hooks/useToast";
import { useMeta } from "../hooks/useMeta";

export function CharacterEditModal({
  gameId, character, onClose, onSaved,
}: {
  gameId: string;
  character: FullCharacter;
  onClose: () => void;
  onSaved: (ch: FullCharacter) => void;
}) {
  const toast = useToast();
  const { meta } = useMeta();
  const [name, setName] = useState(character.name);
  // Seed from the TRUE base score (pre-racial/item) via score_breakdown so re-saving
  // doesn't bake racial bonuses into the base. Falls back to total if unavailable.
  const [scores, setScores] = useState<Record<AbilityKey, number>>(() => {
    const s = {} as Record<AbilityKey, number>;
    for (const a of ABILITY_KEYS) s[a] = character.score_breakdown?.[a]?.base ?? character.scores[a];
    return s;
  });
  const [skills, setSkills] = useState<string[]>(character.skill_proficiencies || []);
  const [expertise, setExpertise] = useState<string[]>(character.skill_expertise || []);
  const [maxHp, setMaxHp] = useState<string>(String(character.max_hp));
  const [notes, setNotes] = useState(character.notes || "");
  const [feats, setFeats] = useState<string[]>((character.feats || []).map((f) => f.index));
  const [featOptions, setFeatOptions] = useState<{ index: string; name: string }[]>([]);
  // Spell editing — seed from the character's current known spells.
  const [cantrips, setCantrips] = useState<string[]>(
    (character.spells || []).filter((s) => s.level === 0).map((s) => s.index));
  const [knownSpells, setKnownSpells] = useState<string[]>(
    (character.spells || []).filter((s) => s.level > 0).map((s) => s.index));
  const [spellsByLevel, setSpellsByLevel] = useState<Record<string, { index: string; name: string; school?: string }[]>>({});
  const [spellsLoading, setSpellsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Rebuild (structural swaps) — seed from the character's current build.
  const cls0 = character.classes[0];
  const [rRace, setRRace] = useState(character.race);
  const [rSubrace, setRSubrace] = useState(character.subrace || "");
  const [rClass, setRClass] = useState(cls0?.class || "");
  const [rSubclass, setRSubclass] = useState(cls0?.subclass || "");
  const [rBg, setRBg] = useState(character.background || "");
  const [rebuilding, setRebuilding] = useState(false);

  const subraceOpts = (meta?.subraces || []).filter((s) => s.race === rRace);
  const subclassOpts = (meta?.classes.find((c) => c.index === rClass)?.subclasses) || [];

  useEffect(() => {
    api.feats().then((r) => setFeatOptions(r.feats)).catch(() => {});
  }, []);

  // Load the class spell list so spells can be edited. Uses the first class with a
  // spell list (covers single-class casters; the engine validates legality on save).
  const casterClass = character.classes.find((c) => c.class)?.class || cls0?.class || "";
  useEffect(() => {
    if (!casterClass) return;
    setSpellsLoading(true);
    api.classSpells(casterClass)
      .then((r) => setSpellsByLevel(r.by_level || {}))
      .catch(() => setSpellsByLevel({}))
      .finally(() => setSpellsLoading(false));
  }, [casterClass]);
  const hasSpellList = Object.keys(spellsByLevel).length > 0;

  function toggle(list: string[], set: (v: string[]) => void, key: string) {
    set(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  }

  async function save() {
    setBusy(true);
    try {
      const patch: EditCharacterPatch = {
        name: name.trim() || character.name,
        scores,
        skill_proficiencies: skills,
        skill_expertise: expertise.filter((s) => skills.includes(s)),
        feats,
        max_hp_override: Number(maxHp) > 0 ? Number(maxHp) : null,
        notes,
      };
      if (hasSpellList) {
        patch.cantrips = cantrips;
        patch.spells = knownSpells;
      }
      const r = await api.editCharacter(gameId, character.id, patch);
      toast.push("Character updated", "success");
      onSaved(r.character);
      onClose();
    } catch (e) {
      toast.push(e instanceof ApiError ? e.message : "Edit failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function rebuild() {
    setRebuilding(true);
    try {
      const patch: import("../lib/types").RebuildPatch = {};
      if (rRace !== character.race) patch.race_index = rRace;
      if (rSubrace !== (character.subrace || "")) patch.subrace_index = rSubrace;
      if (rClass !== (cls0?.class || "")) patch.class_index = rClass;
      if (rSubclass !== (cls0?.subclass || "")) patch.subclass_index = rSubclass;
      if (rBg !== (character.background || "")) patch.background_index = rBg;
      if (Object.keys(patch).length === 0) { toast.push("No structural changes selected", "info"); return; }
      const r = await api.rebuildCharacter(gameId, character.id, patch);
      toast.push(`Rebuilt: ${r.changed.join(", ")}`, "success");
      onSaved(r.character);
      onClose();
    } catch (e) {
      toast.push(e instanceof ApiError ? e.message : "Rebuild failed", "error");
    } finally {
      setRebuilding(false);
    }
  }

  const skillKeys = Object.keys(SKILL_LABELS);

  return (
    <Modal onClose={onClose}>
      <h2>Edit {character.name}</h2>
      <div className="modal__sub">
        Adjust core stats. Derived values (AC, saves, spell DC, skill bonuses) recompute automatically.
      </div>

      <div className="form-row">
        <label className="label">Name</label>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="form-row">
        <label className="label">Ability Scores <span className="hint-inline">(base, before racial bonuses)</span></label>
        <div className="score-grid">
          {ABILITY_KEYS.map((ab) => (
            <div className="score-box" key={ab}>
              <div className="ab">{ABILITY_ABBR[ab]}</div>
              <input className="field edit-score" type="number" min={1} max={30} value={scores[ab]}
                onChange={(e) => setScores({ ...scores, [ab]: Number(e.target.value) })} />
              <div className="mod">{signed(abilityMod(scores[ab]))}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="form-row">
        <label className="label">Max HP <span className="hint-inline">(override; recompute by clearing to 0)</span></label>
        <input className="field" type="number" min={0} value={maxHp}
          onChange={(e) => setMaxHp(e.target.value)} style={{ maxWidth: 120 }} />
      </div>

      <div className="form-row">
        <label className="label">Skill Proficiencies <span className="pick-count">({skills.length})</span></label>
        <div className="pick-grid">
          {skillKeys.map((sk) => (
            <button key={sk} type="button" className={`pick${skills.includes(sk) ? " pick--on" : ""}`}
              onClick={() => toggle(skills, setSkills, sk)}>{SKILL_LABELS[sk]}</button>
          ))}
        </div>
      </div>

      {skills.length > 0 && (
        <div className="form-row">
          <label className="label">Expertise <span className="hint-inline">(double proficiency)</span></label>
          <div className="pick-grid">
            {skills.map((sk) => (
              <button key={sk} type="button" className={`pick pick--gold${expertise.includes(sk) ? " pick--on" : ""}`}
                onClick={() => toggle(expertise, setExpertise, sk)}>{SKILL_LABELS[sk]}</button>
            ))}
          </div>
        </div>
      )}

      {featOptions.length > 0 && (
        <div className="form-row">
          <label className="label">Feats <span className="pick-count">({feats.length})</span></label>
          <div className="pick-grid">
            {featOptions.map((f) => (
              <button key={f.index} type="button" className={`pick${feats.includes(f.index) ? " pick--on" : ""}`}
                onClick={() => toggle(feats, setFeats, f.index)}>{f.name}</button>
            ))}
          </div>
        </div>
      )}

      {(hasSpellList || spellsLoading) && (
        <div className="form-row">
          <label className="label">
            Spells <span className="hint-inline">({titleizeSlug(casterClass)} list — tap to add/remove)</span>
          </label>
          {spellsLoading && <div className="muted" style={{ fontSize: 13 }}>Loading spell list…</div>}
          {hasSpellList && (
            <div className="spell-edit">
              {Object.keys(spellsByLevel)
                .map(Number)
                .sort((a, b) => a - b)
                .map((lvl) => {
                  const list = spellsByLevel[String(lvl)] || [];
                  const isCantrip = lvl === 0;
                  const picked = isCantrip ? cantrips : knownSpells;
                  const setter = isCantrip ? setCantrips : setKnownSpells;
                  const n = list.filter((s) => picked.includes(s.index)).length;
                  return (
                    <div key={lvl} className="spell-edit__group">
                      <div className="spell-edit__lvl">
                        {isCantrip ? "Cantrips" : `Level ${lvl}`}
                        <span className="pick-count"> ({n})</span>
                      </div>
                      <div className="pick-grid">
                        {list.map((s) => (
                          <button key={s.index} type="button"
                            className={`pick${picked.includes(s.index) ? " pick--on" : ""}`}
                            title={s.school || ""}
                            onClick={() => toggle(picked, setter, s.index)}>{s.name}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          <div className="hint-inline" style={{ marginTop: 6 }}>
            Tip: slots and spells known/prepared limits aren't auto-enforced here — pick the loadout you want.
          </div>
        </div>
      )}

      <div className="form-row">
        <label className="label">Notes</label>
        <textarea className="field" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Backstory, bonds, reminders…" rows={3} />
      </div>

      <div className="modal__row">
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {meta && (
        <div className="rebuild-block">
          <div className="rebuild-block__head">Rebuild — swap structural choices</div>
          <div className="modal__sub" style={{ marginTop: 0 }}>
            Keeps level, ability scores, HP, inventory, and spells. Re-derives what the choice affects.
            Swapping race or class clears the subrace/subclass tied to it.
          </div>
          <div className="form-grid2">
            <div className="builder__section">
              <label className="label">Race</label>
              <select className="field" value={rRace} onChange={(e) => { setRRace(e.target.value); setRSubrace(""); }}>
                {meta.races.map((r) => <option key={r.index} value={r.index}>{r.name}</option>)}
              </select>
              {subraceOpts.length > 0 && (
                <select className="field mt8" value={rSubrace} onChange={(e) => setRSubrace(e.target.value)}>
                  <option value="">— no subrace —</option>
                  {subraceOpts.map((s) => <option key={s.index} value={s.index}>{s.name}</option>)}
                </select>
              )}
            </div>
            <div className="builder__section">
              <label className="label">Class</label>
              <select className="field" value={rClass} onChange={(e) => { setRClass(e.target.value); setRSubclass(""); }}>
                {meta.classes.map((c) => <option key={c.index} value={c.index}>{c.name}</option>)}
              </select>
              {subclassOpts.length > 0 && (
                <select className="field mt8" value={rSubclass} onChange={(e) => setRSubclass(e.target.value)}>
                  <option value="">— no subclass —</option>
                  {subclassOpts.map((s) => <option key={s.index} value={s.index}>{s.name}</option>)}
                </select>
              )}
            </div>
          </div>
          <div className="form-row">
            <label className="label">Background</label>
            <select className="field" value={rBg} onChange={(e) => setRBg(e.target.value)}>
              {meta.backgrounds.map((b) => <option key={b.index} value={b.index}>{b.name}</option>)}
            </select>
          </div>
          <div className="modal__row">
            <button className="btn btn--gold" disabled={rebuilding} onClick={rebuild}>
              {rebuilding ? "Rebuilding…" : "Apply Rebuild"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
